import React from 'react';

const RELOAD_KEY = 'qg_chunk_reload_at';

function postErrorToServer(error, info) {
  try {
    fetch('/api/user/error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        message: error?.message || String(error),
        stack: error?.stack || '',
        page_url: typeof window !== 'undefined' ? window.location.pathname : '',
        component: info?.componentStack ? info.componentStack.trim().split('\n')[1]?.trim() || 'ErrorBoundary' : 'ErrorBoundary',
        extra: { componentStack: info?.componentStack || '' },
      }),
    }).catch(() => {});
  } catch (_) {}
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    postErrorToServer(error, info);
    console.error('[ErrorBoundary] Caught error:', error, info?.componentStack);

    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(error?.message || '') ||
      /loading css chunk/i.test(error?.message || '');

    if (isChunkError) {
      const lastReload = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
      const now = Date.now();
      if (now - lastReload > 15000) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        this.setState({ reloading: true });
        window.location.reload();
      }
    } else {
      this.setState({ errorInfo: info });
    }
  }

  handleReload = () => {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  };

  handleGoBack = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    try { window.history.back(); } catch { window.location.href = '/dashboard'; }
  };

  render() {
    if (this.state.reloading) {
      return (
        <div style={{
          minHeight: '100vh', background: '#0a0a0a',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'sans-serif',
        }}>
          <div style={{ textAlign: 'center', color: '#C9A84C' }}>
            <div style={{
              width: 36, height: 36, border: '3px solid #C9A84C',
              borderTopColor: 'transparent', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
            }} />
            <p style={{ color: '#fff', margin: 0 }}>Refreshing…</p>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }

    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === 'development';
      const errMsg = this.state.error?.message || String(this.state.error || '');
      return (
        <div style={{
          minHeight: '100vh', background: '#0a0a0a',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'sans-serif', padding: 24,
        }}>
          <div style={{
            maxWidth: 480, width: '100%', textAlign: 'center',
            border: '1px solid rgba(201,168,76,0.2)', borderRadius: 16,
            padding: '40px 32px', background: 'rgba(255,255,255,0.03)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ color: '#fff', margin: '0 0 8px', fontSize: 20 }}>
              Something went wrong
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0 24px', fontSize: 14 }}>
              The page encountered an unexpected error. Going back or refreshing usually fixes it.
            </p>
            {isDev && errMsg && (
              <div style={{
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 10, padding: '12px 16px', marginBottom: 20, textAlign: 'left',
              }}>
                <p style={{ color: '#f87171', fontSize: 11, fontFamily: 'monospace', margin: 0, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {errMsg}
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={this.handleGoBack}
                style={{
                  background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8, padding: '10px 22px', fontSize: 14,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                ← Go Back
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  background: '#C9A84C', color: '#000', border: 'none',
                  borderRadius: 8, padding: '10px 22px', fontSize: 14,
                  fontWeight: 600, cursor: 'pointer', letterSpacing: '0.03em',
                }}
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
